import ImsRole from '../enums/ims-role.enum';
import { DapAccessGrant, ClaimKey, ClaimsModel } from './claims.model';
import { LoginTokenModel } from './login.token.model';
import * as _ from 'lodash-es';

export class UserLoginModel {
  public static readonly GUEST = 'guest';
  public claims: ClaimsModel;
  public loginToken: LoginTokenModel;

  constructor(claims: ClaimsModel, loginToken: LoginTokenModel) {
    this.claims = claims;
    this.loginToken = loginToken;
  }

  public isGuestUser(): boolean {
    return this.getClaimValue(ClaimKey.IS_ANONYMOUS) === UserLoginModel.GUEST;
  }

  public isSharedUser(): boolean {
    return 'true' === this.getClaimValue(ClaimKey.USER_IS_GUEST);
  }

  public isAuthenticatedUser(): boolean {
    return (!this.isGuestUser() && !this.isSharedUser());
  }

  public isDistrictUser() : boolean {
    return 'true' === this.getClaimValue(ClaimKey.DISTRICT_USER);
  }

  public isDestinyAdmin() : boolean {
    return 'true' === this.getClaimValue(ClaimKey.DESTINY_ADMIN);
  }
  public isDestinyAdminAtSite() : boolean {
    return !!(this.isDestinyAdmin() && this.getSiteGuid());
  }
  public isDestinyAdminNotAtSite() : boolean {
    return !!(this.isDestinyAdmin() && !this.getSiteGuid());
  }

  public isDistrictUserAtSite(): boolean {
    return !!(this.isDistrictUser()  && !this.isDestinyAdmin() && this.getSiteGuid());
  }

  public isDistrictUserNotAtSite(): boolean {
    return !!(this.isDistrictUser() && !this.isDestinyAdmin() && !this.getSiteGuid());
  }

  public getDistrictName(): string {
    const val = this.getClaimValue(ClaimKey.DISTRICT_NAME);
    return (val ? val.toString() : undefined);
  }

  public getSiteGuid(): string {
    const val = this.getClaimValue(ClaimKey.LOCATION_ID);
    return (val ? val.toString() : undefined);
  }

  /** @see getSub() */
  public getUserGuid(): string {
    const val = this.getClaimValue(ClaimKey.USER_GUID);
    return (val ? val.toString() : undefined);
  }

  public getAppId(): string {
    const val = this.getClaimValue(ClaimKey.APP_ID);
    return (val ? val.toString() : undefined);
  }

  public getSub(): string {
    const val = this.getClaimValue(ClaimKey.SUB);
    return (val ? val.toString() : undefined);
  }

  public getLocationName(): string {
    const val = this.getClaimValue(ClaimKey.LOCATION_NAME);
    return (val ? val.toString() : undefined);
  }

  public getFollettShelfId(): string {
    const val = this.getClaimValue(ClaimKey.FOLLETT_SHELF_ID);
    return (val ? val.toString() : undefined);
  }

  public getDistrictFollettShelfId(): string {
    const val = this.getClaimValue(ClaimKey.DISTRICT_FOLLETT_SHELF_ID);
    return (val ? val.toString() : undefined);
  }

  public getFirstName(): string {
    const val = this.getClaimValue(ClaimKey.FIRST_NAME);
    return (val ? val.toString() : undefined);
  }

  public getLastName(): string {
    const val = this.getClaimValue(ClaimKey.LAST_NAME);
    return (val ? val.toString() : undefined);
  }

  public getUserName(): string {
    const val = this.getClaimValue(ClaimKey.USERNAME);
    return (val ? val.toString() : undefined);
  }

  public getPatronType(): string {
    const val = this.getClaimValue(ClaimKey.PATRON_TYPE);
    return (val ? val.toString() : undefined);
  }

  public getDistrictCustomerNumber(): string {
    const val = this.getClaimValue(ClaimKey.FSS_DISTRICT_CUSTOMER_NUMBER);
    return (val ? val.toString() : undefined);
  }
  public getFssSiteCustomerNumber(): string {
    const val = this.getClaimValue(ClaimKey.FSS_SITE_NUMBER);
    return (val ? val.toString() : undefined);
  }
  public getFlrCustomerNumber(): string {
    const val = this.getClaimValue(ClaimKey.FLR_CUSTOMER_NUMBER);
    return (val ? val.toString() : undefined);
  }
  public getRoles(): string {
    const val = this.getClaimValue(ClaimKey.ROLES);
    return (val ? val.toString() : '');
  }

  public getPatronToolbarName(): string {
    // LCP-438 If no first name, show last name. If no last name, show username.
    // Shared user does not have first/last name, so show username.
    if (this.isSharedUser()) {
      return this.getUserName();
    } else {
      let name = this.getFirstName();
      if(!name) {
        name = this.getLastName();
      }
      if(!name) {
        name = this.getUserName();
      }
      return name || '';
    }
  }
  public getPatronName() : string {
    if (this.isGuestUser()) {
      return _.upperFirst(UserLoginModel.GUEST);
    } else if (this.isSharedUser()) {
      return this.getUserName();
    } else {
      const firstName = this.getFirstName() || '';
      const lastName = this.getLastName() || '';
      const spacer = firstName ? ' ' : '';
      return firstName + spacer + lastName;
    }
  }

  public getUserSessionTimeoutSeconds() : number {
    const val = this.getClaimValue(ClaimKey.USER_SESSION_TIMEOUT_SECONDS);
    return (val ? Number(val.toString()) : undefined);
  }

  public isEngageEnabled(): boolean {
    return this.isAccessGrantEnabled(DapAccessGrant.ENGAGE);
  }

  public isCollectionsEnabled(): boolean {
    return this.isAccessGrantEnabled(DapAccessGrant.COLLECTIONS);
  }

  public isClassSetEnabled(): boolean {
    return this.isAccessGrantEnabled(DapAccessGrant.CLASS_SET);
  }

  public isFollettShelfAdmin(): boolean {
    const val = this.getClaimValue(ClaimKey.FOLLETT_SHELF_ADMIN);
    return val === 'true';
  }

  public isDestinyForcePasswordReset(): boolean {
    return 'true' === this.getClaimValue(ClaimKey.DESTINY_FORCE_PASSWORD_RESET);
  }

  private isAccessGrantEnabled(accessGrant: DapAccessGrant): boolean {
    const accessGrants: string = _.get(this.claims, ClaimKey.DAP_ACCESS_GRANTS, '').toString();
    return accessGrants.indexOf(accessGrant) >= 0;
  }

  private getClaimValue(key: ClaimKey): string | number {
    return _.get(this.claims, key);
  }

  public hasRole(roles: ImsRole[] = []): boolean {
    const roleVals: string = this.getRoles();
    return roles.some(r => roleVals.indexOf(r) >= 0);
  }


  public getDestinyVersion() {
    const val = this.getClaimValue(ClaimKey.DESTINY_VERSION);
    return (val ? val.toString() : undefined);
  }
  public getDestinyBuildNumber() {
    const val = this.getClaimValue(ClaimKey.DESTINY_BUILD_NUMBER);
    return (val ? val.toString() : undefined);
  }

  public getCustomerCountry() {
    const val = this.getClaimValue(ClaimKey.CUST_COUNTRY);
    return (val ? val.toString() : undefined);
  }
  public getCustomerState() {
    const val = this.getClaimValue(ClaimKey.CUST_STATE);
    return (val ? val.toString() : undefined);
  }
  public getCustomerCity() {
    const val = this.getClaimValue(ClaimKey.CUST_CITY);
    return (val ? val.toString() : undefined);
  }
  public isUSCustomer() {
    return this.getCustomerCountry() === 'US';
  }

  public doesUserHaveAccessToSettings(): boolean {
    return  this.isDestinyAdmin() ||
            this.isDistrictUser() ||
            this.hasRole([ImsRole.Administrator, ImsRole.Alumni, ImsRole.Faculty, ImsRole.Instructor, ImsRole.Mentor, ImsRole.Other, ImsRole.Staff]);
  }

}
